import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useUploadDocumentsMutation } from "../../api/admin/chatbotApi";

const UploadDocumentModal = ({ open, onClose, agentId, onUploadSuccess }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef();
  const [uploadDocuments] = useUploadDocumentsMutation();

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setSelectedFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) return;
    setLoading(true);

    const filesData = await Promise.all(
      selectedFiles.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              resolve({
                fileName: file.name,
                contentType: file.type,
                bytes: reader.result.split(",")[1],
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
      )
    );

    try {
      await uploadDocuments({ agentId, files: filesData }).unwrap();
      onUploadSuccess && onUploadSuccess();
      onClose();
    } catch (err) {
      alert("Errore nel caricamento del documento");
    } finally {
      setLoading(false);
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <CloudUploadIcon color="primary" sx={{ mr: 1, verticalAlign: "middle" }} />
        Carica documento
      </DialogTitle>
      <DialogContent>
        <Paper
          elevation={dragActive ? 6 : 2}
          sx={{
            border: dragActive ? "2px solid #1976d2" : "2px dashed #bdbdbd",
            p: 3,
            mb: 2,
            textAlign: "center",
            background: dragActive ? "#f0f7ff" : "#fafafa",
            cursor: "pointer",
            transition: "border 0.2s, background 0.2s",
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          <Typography variant="body1" color="textSecondary">
            Trascina qui i file oppure <span style={{ color: "#1976d2", fontWeight: 500 }}>clicca per selezionare</span>
          </Typography>
        </Paper>
        {selectedFiles.length > 0 && (
          <List dense>
            {selectedFiles.map((file, idx) => (
              <ListItem key={file.name + idx} divider>
                <ListItemIcon>
                  <InsertDriveFileIcon color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={file.name}
                  secondary={`${(file.size / 1024).toFixed(1)} KB`}
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={() => handleRemoveFile(idx)}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annulla
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          color="primary"
          disabled={loading || !selectedFiles.length}
        >
          {loading ? <CircularProgress size={24} /> : "Carica"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDocumentModal;